import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import SendIcon from "@material-ui/icons/Send";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import BlockIcon from "@material-ui/icons/Block";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PersonIcon from "@material-ui/icons/Person";
import AndroidIcon from "@material-ui/icons/Android";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import { previewProposedActionNeedsUserApproval } from "../../features/agents/previewApproval";
import CollapsibleValueView from "./CollapsibleValueView";
import AgentCoreLiveView, { buildLiveViewPageUrl } from "./AgentCoreLiveView";

const idxKey = (i) => `__idx_${i}`;

/** @typedef {{ formUrl: string|null, workflowId: string, listening?: boolean, preparing?: boolean, error?: string|null, latestSubmission?: object|null, timedOut?: boolean }} FormSampleWaitState */

/** Parse JSON-looking strings so Browser Task / nested payloads stay collapsible. */
const coercePreviewValue = (value) => {
  if (value == null) return value;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) return value;
  try {
    return JSON.parse(trimmed);
  } catch (_) {
    return value;
  }
};

/** Abbreviate huge scalars (e.g. base64) in the UI only — wire payload stays full. */
const formatPreviewScalar = (value) => {
  if (value == null || value === "") return "—";
  const s = String(value);
  if (s.length <= 280) return s;
  return `${s.slice(0, 96)}… [${s.length.toLocaleString()} chars]`;
};

const INITIAL_PREVIEW_PHASES = [
  { id: "save", label: "Save agent" },
  { id: "load_agent", label: "Load agent" },
  { id: "fetch_sample", label: "Fetch sample trigger" },
  { id: "wait_form", label: "Waiting for form submission" },
  { id: "enrich_files", label: "Read attachments" },
  { id: "brain", label: "Plan actions" },
];

const FOLLOWUP_PREVIEW_PHASES = [
  { id: "run_actions", label: "Run approved actions" },
  { id: "enrich_outputs", label: "Read action results" },
  { id: "enrich_files", label: "Read attachments" },
  { id: "brain", label: "Plan next actions" },
];

const PreviewAgentModal = ({
  open,
  onClose,
  runId,
  steps = [],
  currentReasoning = "",
  currentProposedActions = [],
  onRunApproved,
  useProposalIndexAsSelection = false,
  previewApprovalEpoch = 0,
  /** Agent config (for classifying read vs mutating tools in brain preview). */
  agent = null,
  onSendReply = null,
  loading = false,
  runResult = null,
  previewError = null,
  /** { id, message } while preview is preparing or advancing. */
  previewPhase = null,
  /** Phase D: Live View handoff when Browser RPA needs MFA/CAPTCHA. */
  userActionPause = null,
  onContinueUserAction = null,
  continuingUserAction = false,
  /** Workload Forms: wait for a real submission before brain preview. */
  formSampleWait = null,
  onStartFormListen = null,
  onUseLatestFormSample = null,
  onCancelFormSampleWait = null,
  onRetryFormListen = null,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  const [decisions, setDecisions] = useState({});
  const [replyText, setReplyText] = useState("");
  const [copiedFormUrl, setCopiedFormUrl] = useState(false);

  useEffect(() => {
    if (open) setDecisions({});
  }, [open, runId, previewApprovalEpoch]);

  useEffect(() => {
    if (!formSampleWait) setCopiedFormUrl(false);
  }, [formSampleWait]);

  const actions = currentProposedActions || [];

  const rowNeedsUserApproval = useMemo(() => {
    return (item) => {
      if (!useProposalIndexAsSelection || !agent) return true;
      return previewProposedActionNeedsUserApproval(item, agent);
    };
  }, [useProposalIndexAsSelection, agent]);

  const approvedIds = actions.filter((a) => decisions[a.action_id] === "approved").map((a) => a.action_id);

  const mutatingIndices = useMemo(
    () => actions.map((item, i) => (rowNeedsUserApproval(item) ? i : null)).filter((x) => x !== null),
    [actions, rowNeedsUserApproval],
  );

  const allMutatingDecided =
    mutatingIndices.length === 0 ||
    mutatingIndices.every((i) => {
      const d = decisions[idxKey(i)];
      return d === "approved" || d === "skipped";
    });

  const indicesToRun = useMemo(() => {
    const out = [];
    for (let i = 0; i < actions.length; i++) {
      const item = actions[i];
      if (!rowNeedsUserApproval(item)) out.push(i);
      else if (decisions[idxKey(i)] === "approved") out.push(i);
    }
    return out;
  }, [actions, decisions, rowNeedsUserApproval]);

  const canRunBrain =
    useProposalIndexAsSelection && allMutatingDecided && indicesToRun.length > 0;
  const canRunLegacy = !useProposalIndexAsSelection && approvedIds.length > 0;
  const pausedForUser = Boolean(userActionPause?.liveViewUrl || userActionPause?.needsUserAction);
  const canRun = (canRunBrain || canRunLegacy) && !pausedForUser;

  const showRunResult = runResult !== null && runResult !== undefined;
  const success = runResult && runResult.success;
  const resultCount = runResult && runResult.results ? runResult.results.length : 0;
  const hasCurrentActions = Array.isArray(currentProposedActions) && currentProposedActions.length > 0;
  const canReply = typeof onSendReply === "function";
  const waitingForFormSample = Boolean(formSampleWait);
  const formListening = Boolean(formSampleWait?.listening);
  const formPreparing = Boolean(formSampleWait?.preparing);
  const phaseId = previewPhase?.id || null;
  const isInitialPrep =
    (loading || waitingForFormSample) &&
    !hasCurrentActions &&
    steps.length === 0 &&
    !showRunResult &&
    !pausedForUser &&
    !currentReasoning;
  const agentDidList = Array.isArray(userActionPause?.instructions?.agentIsDoing)
    ? userActionPause.instructions.agentIsDoing
    : Array.isArray(userActionPause?.agentDid)
      ? userActionPause.agentDid
      : [];
  const youNeedList = Array.isArray(userActionPause?.instructions?.youNeedTo)
    ? userActionPause.instructions.youNeedTo
    : [];
  const phaseList = isInitialPrep ? INITIAL_PREVIEW_PHASES : FOLLOWUP_PREVIEW_PHASES;
  const activePhaseIndex = phaseId ? phaseList.findIndex((p) => p.id === phaseId) : -1;

  const handleApprove = (key) => setDecisions((prev) => ({ ...prev, [key]: "approved" }));
  const handleSkip = (key) => setDecisions((prev) => ({ ...prev, [key]: "skipped" }));
  const handleRunApproved = () => {
    if (useProposalIndexAsSelection) {
      onRunApproved(runId, indicesToRun);
    } else {
      onRunApproved(runId, approvedIds);
    }
  };
  const handleSendReply = () => {
    const msg = (replyText || "").trim();
    if (!msg || !onSendReply) return;
    onSendReply(msg);
    setReplyText("");
  };

  const renderStatusPanel = () => {
    if (!loading && !previewPhase) return null;
    const message =
      (previewPhase && previewPhase.message) ||
      (loading ? "Working…" : "");
    if (!message && !loading) return null;
    const activeColor = theme.palette.text.primary;
    const doneColor = isDark ? "rgba(255,255,255,0.72)" : "rgba(0,0,0,0.55)";
    const pendingColor = isDark ? "rgba(255,255,255,0.42)" : "rgba(0,0,0,0.32)";
    return (
      <Paper
        variant="outlined"
        style={{
          padding: 14,
          marginBottom: 16,
          backgroundColor: isDark ? "rgba(144, 202, 249, 0.12)" : "rgba(25, 118, 210, 0.06)",
          borderColor: isDark ? "rgba(144, 202, 249, 0.35)" : "rgba(25, 118, 210, 0.25)",
        }}
      >
        <Box display="flex" alignItems="flex-start" style={{ gap: 12 }}>
          {loading ? <CircularProgress size={22} style={{ marginTop: 2, flexShrink: 0 }} /> : null}
          <Box flex={1} minWidth={0}>
            <Typography variant="subtitle2" style={{ marginBottom: 4, color: activeColor }}>
              {message || "Working…"}
            </Typography>
            {phaseId === "enrich_files" ? (
              <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
                Large PDFs and fax attachments can take a while to download and extract text.
              </Typography>
            ) : null}
            {activePhaseIndex >= 0 ? (
              <Box mt={1}>
                {phaseList.map((phase, idx) => {
                  const done = idx < activePhaseIndex;
                  const active = idx === activePhaseIndex;
                  return (
                    <Typography
                      key={phase.id}
                      variant="caption"
                      display="block"
                      style={{
                        color: active ? activeColor : done ? doneColor : pendingColor,
                        fontWeight: active ? 600 : 400,
                        marginBottom: 2,
                      }}
                    >
                      {done ? "✓" : active ? "→" : "·"} {phase.label}
                    </Typography>
                  );
                })}
              </Box>
            ) : null}
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderReasoningBlock = (content, label = "Thinking") => (
    <Paper variant="outlined" style={{ padding: 12, marginBottom: 12, backgroundColor: "rgba(0,0,0,0.02)" }}>
      <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 6 }}>
        <AndroidIcon style={{ fontSize: 16, color: "text.secondary" }} />
        <Typography variant="caption" color="textSecondary">{label}</Typography>
      </Box>
      <Typography variant="body2">{content}</Typography>
    </Paper>
  );

  const renderProposedActionsList = (list, showButtons = false) => (
    <Box mb={2}>
      <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
        Proposed actions
      </Typography>
      {(list || []).map((item, idx) => {
        const decKey = showButtons && useProposalIndexAsSelection ? idxKey(idx) : item.action_id;
        const needsApr = !showButtons || !useProposalIndexAsSelection ? true : rowNeedsUserApproval(item);
        const decided =
          showButtons && needsApr && (decisions[decKey] === "approved" || decisions[decKey] === "skipped");
        const approvalControls =
          showButtons && needsApr ? (
            !decided ? (
              <Box display="flex" alignItems="center" style={{ gap: 8, flexShrink: 0 }}>
                <Button
                  type="button"
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => handleApprove(decKey)}
                >
                  Approve
                </Button>
                <Button
                  type="button"
                  size="small"
                  variant="outlined"
                  startIcon={<BlockIcon />}
                  onClick={() => handleSkip(decKey)}
                >
                  Skip
                </Button>
              </Box>
            ) : (
              <Chip
                size="small"
                label={decisions[decKey] === "approved" ? "Approved" : "Skipped"}
                color={decisions[decKey] === "approved" ? "primary" : "default"}
              />
            )
          ) : showButtons && !needsApr && useProposalIndexAsSelection ? (
            <Chip size="small" label="Read / lookup — included when you Run" variant="outlined" />
          ) : null;

        return (
          <Paper
            key={useProposalIndexAsSelection ? `prop-${idx}-${item.action_id}` : item.action_id}
            variant="outlined"
            style={{ padding: 14, marginBottom: 12 }}
          >
            <Box
              display="flex"
              alignItems="flex-start"
              justifyContent="space-between"
              flexWrap="wrap"
              style={{ gap: 8 }}
            >
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 4, minWidth: 0, flex: "1 1 200px" }}>
                {item.app_icon ? (
                  <Avatar src={item.app_icon} style={{ width: 24, height: 24, marginRight: 4 }} />
                ) : null}
                <Typography variant="subtitle2">
                  {item.app_label || "App"}: {item.label}
                </Typography>
                {item.rule_name ? (
                  <Chip size="small" label={item.rule_name} style={{ marginLeft: 4 }} variant="outlined" />
                ) : null}
              </Box>
              {approvalControls}
            </Box>

            {item.payload_preview && item.payload_preview.length > 0 ? (
              <Box mt={1.5} width="100%">
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 6 }}>
                  Data to be sent
                </Typography>
                <Box
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: 10,
                    borderRadius: 6,
                    backgroundColor: "rgba(0,0,0,0.03)",
                    border: "1px solid rgba(0,0,0,0.06)",
                  }}
                >
                  {item.payload_preview.map((line, i) => {
                    const value = coercePreviewValue(line.value);
                    const complex =
                      (value != null && typeof value === "object") || Array.isArray(value);
                    return (
                      <Box
                        key={i}
                        style={{
                          width: "100%",
                          marginTop: i === 0 ? 0 : 10,
                          paddingTop: i === 0 ? 0 : 10,
                          borderTop: i === 0 ? "none" : "1px solid rgba(0,0,0,0.06)",
                        }}
                      >
                        {complex ? (
                          <CollapsibleValueView
                            value={value}
                            label={line.name}
                            defaultOpen={false}
                            depth={0}
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            component="div"
                            style={{ fontWeight: 600, wordBreak: "break-word" }}
                          >
                            {line.name}
                            <Typography
                              variant="body2"
                              component="span"
                              style={{ fontWeight: 400, marginLeft: 6, wordBreak: "break-word" }}
                            >
                              {value == null || value === ""
                                ? "—"
                                : formatPreviewScalar(value)}
                            </Typography>
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ) : null}
          </Paper>
        );
      })}
    </Box>
  );

  const handleDialogClose = (_event, reason) => {
    // Only Cancel/Close buttons may dismiss — ignore backdrop + Escape.
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    if (loading && !waitingForFormSample) return;
    if (waitingForFormSample && typeof onCancelFormSampleWait === "function") {
      onCancelFormSampleWait();
    }
    if (typeof onClose === "function") onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown
      maxWidth={pausedForUser ? "lg" : "md"}
      fullWidth
    >
      <DialogTitle>Preview Agent</DialogTitle>
      <DialogContent dividers>
        {renderStatusPanel()}

        {!isInitialPrep ? (
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Step through each batch: lookups and reads run together when you click Run (no per-step approval).
            Approve or skip only actions that create, update, delete, or send data.
          </Typography>
        ) : waitingForFormSample ? (
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            Open your form, submit a test entry, and Preview will continue with that submission as sample trigger data.
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            Preparing a dry run with sample trigger data. Proposed actions will appear here when ready.
          </Typography>
        )}

        {waitingForFormSample && (
          <Paper
            variant="outlined"
            style={{
              padding: 14,
              marginBottom: 16,
              backgroundColor: isDark ? "rgba(144, 202, 249, 0.12)" : "rgba(25, 118, 210, 0.06)",
              borderColor: isDark ? "rgba(144, 202, 249, 0.35)" : "rgba(25, 118, 210, 0.25)",
            }}
          >
            <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
              Submit a test form entry
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
              Open the form URL below, fill it out, and submit. We&apos;ll detect the submission and continue Preview.
            </Typography>
            {formSampleWait.formUrl ? (
              <Typography
                variant="body2"
                component="div"
                style={{
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  fontSize: 13,
                  wordBreak: "break-all",
                  marginBottom: 12,
                  padding: "8px 10px",
                  borderRadius: 6,
                  backgroundColor: isDark ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.04)",
                }}
              >
                {formSampleWait.formUrl}
              </Typography>
            ) : null}
            {formSampleWait.error ? (
              <Typography variant="body2" color="error" style={{ marginBottom: 12 }}>
                {formSampleWait.error}
              </Typography>
            ) : null}
            {formListening || formPreparing ? (
              <Box display="flex" alignItems="center" style={{ gap: 10, marginBottom: 12 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">
                  {formPreparing
                    ? "Preparing form…"
                    : formSampleWait.timedOut
                      ? "Still listening… submit the form, or retry."
                      : "Listening for a submission…"}
                </Typography>
              </Box>
            ) : null}
            <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<OpenInNewIcon />}
                disabled={!formSampleWait.formUrl || formPreparing}
                onClick={() => {
                  if (formSampleWait.formUrl) {
                    window.open(formSampleWait.formUrl, "_blank", "noopener,noreferrer");
                  }
                  if (typeof onStartFormListen === "function") onStartFormListen();
                }}
                style={{ textTransform: "none" }}
              >
                {formListening ? "Open form again" : "Open form"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileCopyOutlinedIcon />}
                disabled={!formSampleWait.formUrl}
                onClick={() => {
                  const url = formSampleWait.formUrl;
                  if (!url) return;
                  if (navigator.clipboard?.writeText) {
                    navigator.clipboard.writeText(url).then(() => {
                      setCopiedFormUrl(true);
                      setTimeout(() => setCopiedFormUrl(false), 2000);
                    }).catch(() => {});
                  }
                }}
                style={{ textTransform: "none" }}
              >
                {copiedFormUrl ? "Copied" : "Copy link"}
              </Button>
              {formSampleWait.latestSubmission?.payload && typeof onUseLatestFormSample === "function" ? (
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={formPreparing || loading}
                  onClick={() => onUseLatestFormSample(formSampleWait.latestSubmission.payload)}
                  style={{ textTransform: "none" }}
                >
                  Use latest submission
                </Button>
              ) : null}
              {formSampleWait.timedOut && typeof onRetryFormListen === "function" ? (
                <Button
                  variant="text"
                  onClick={() => onRetryFormListen()}
                  style={{ textTransform: "none" }}
                >
                  Retry listening
                </Button>
              ) : null}
            </Box>
            {!formListening && !formPreparing ? (
              <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 10 }}>
                Tip: click Open form to start listening automatically.
              </Typography>
            ) : null}
          </Paper>
        )}

        {steps.length > 0 && (
          <Box mb={2}>
            <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
              Run timeline
            </Typography>
            <Box style={{ maxHeight: 280, overflowY: "auto" }}>
              {steps.map((step, sidx) => {
                if (step.type === "reasoning") {
                  return <Box key={sidx}>{renderReasoningBlock(step.content)}</Box>;
                }
                if (step.type === "proposed_actions") {
                  return <Box key={sidx}>{renderProposedActionsList(step.actions)}</Box>;
                }
                if (step.type === "user_message") {
                  return (
                    <Paper key={sidx} variant="outlined" style={{ padding: 10, marginBottom: 12, backgroundColor: "rgba(25, 118, 210, 0.06)" }}>
                      <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 4 }}>
                        <PersonIcon style={{ fontSize: 16 }} color="primary" />
                        <Typography variant="caption" color="textSecondary">You</Typography>
                      </Box>
                      <Typography variant="body2">{step.content}</Typography>
                    </Paper>
                  );
                }
                if (step.type === "actions_run") {
                  const sr = step.runResult;
                  const ok = sr && sr.success;
                  const ran = Array.isArray(step.ranActions) ? step.ranActions : [];
                  const header =
                    ran.length === 1
                      ? ran[0].label
                      : ran.length > 1
                        ? `Ran ${ran.length} actions`
                        : `Ran ${step.approvedIds?.length ?? 0} action(s)`;
                  const statusLine = (row) => {
                    if (row.needsUserAction) {
                      const reason = row.reason || row.instructions?.reason || "";
                      if (reason === "task_step_failed" || reason === "task_failed") {
                        return "Paused for Live View (stuck step — finish it, then Continue).";
                      }
                      return "Paused for Live View (login / MFA / assist).";
                    }
                    if (row.success) {
                      if (row.pageHint) {
                        return `Completed. Open: ${row.pageHint}${
                          row.pageNote ? ` — ${row.pageNote}` : ""
                        }`;
                      }
                      return "Completed successfully.";
                    }
                    return row.error || sr?.error || "Failed.";
                  };
                  return (
                    <Paper
                      key={sidx}
                      variant="outlined"
                      style={{
                        padding: 10,
                        marginBottom: 12,
                        backgroundColor: ok ? "rgba(76, 175, 80, 0.08)" : "rgba(244, 67, 54, 0.08)",
                        borderColor: ok ? "rgba(76, 175, 80, 0.3)" : "rgba(244, 67, 54, 0.3)",
                      }}
                    >
                      <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 4 }}>
                        <PlayArrowIcon style={{ fontSize: 16 }} />
                        <Typography variant="caption" color="textSecondary">
                          {header}
                        </Typography>
                      </Box>
                      {ran.length > 0 ? (
                        ran.map((row, ri) => (
                          <Box key={`ran-${sidx}-${ri}`} style={{ marginBottom: ri < ran.length - 1 ? 8 : 0 }}>
                            {ran.length > 1 && (
                              <Typography variant="body2" style={{ fontWeight: 600 }}>
                                {row.label}
                              </Typography>
                            )}
                            <Typography
                              variant="body2"
                              color={!row.success && !row.needsUserAction ? "error" : "textPrimary"}
                              style={{
                                wordBreak: "break-word",
                                maxHeight: 72,
                                overflow: "hidden",
                              }}
                            >
                              {statusLine(row)}
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2">
                          {ok
                            ? (sr.results?.length
                                ? `${sr.results.length} action(s) completed successfully.`
                                : "Run completed.")
                            : (sr?.error || "Run failed.")}
                        </Typography>
                      )}
                    </Paper>
                  );
                }
                if (step.type === "cycle_complete") {
                  return (
                    <Paper key={sidx} variant="outlined" style={{ padding: 10, marginBottom: 12, backgroundColor: "rgba(0,0,0,0.03)" }}>
                      <Typography variant="body2" color="textSecondary">{step.content || "Run cycle complete."}</Typography>
                    </Paper>
                  );
                }
                return null;
              })}
            </Box>
          </Box>
        )}

        {previewError && (
          <Paper variant="outlined" style={{ padding: 12, marginBottom: 16, backgroundColor: "rgba(244, 67, 54, 0.08)" }}>
            <Typography variant="body2" color="error">{previewError}</Typography>
          </Paper>
        )}

        {pausedForUser && (
          <Paper
            variant="outlined"
            style={{
              padding: 14,
              marginBottom: 16,
              backgroundColor: isDark ? "rgba(33, 150, 243, 0.12)" : "rgba(33, 150, 243, 0.06)",
              borderColor: "rgba(33, 150, 243, 0.35)",
            }}
          >
            <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
              Browser login needs you
            </Typography>
            {agentDidList.length > 0 && (
              <Box mb={1.5}>
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 4 }}>
                  Agent is doing
                </Typography>
                {agentDidList.map((line, i) => (
                  <Typography key={`agent-did-${i}`} variant="body2" style={{ marginBottom: 2 }}>
                    • {line}
                  </Typography>
                ))}
              </Box>
            )}
            {youNeedList.length > 0 && (
              <Box mb={1.5}>
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 4 }}>
                  You need to
                </Typography>
                {youNeedList.map((line, i) => (
                  <Typography key={`you-need-${i}`} variant="body2" style={{ marginBottom: 2 }}>
                    • {line}
                  </Typography>
                ))}
              </Box>
            )}
            {userActionPause?.liveViewUrl ? (
              <Box mb={1.5}>
                <AgentCoreLiveView
                  signedUrl={userActionPause.liveViewUrl}
                  remoteWidth={
                    Number(userActionPause.viewportWidth) ||
                    Number(userActionPause.liveViewViewport?.width) ||
                    1440
                  }
                  remoteHeight={
                    Number(userActionPause.viewportHeight) ||
                    Number(userActionPause.liveViewViewport?.height) ||
                    900
                  }
                  height="min(62vh, 640px)"
                />
                <Button
                  size="small"
                  color="primary"
                  style={{ marginTop: 8 }}
                  onClick={() => {
                    const href = buildLiveViewPageUrl(
                      userActionPause.liveViewUrl,
                      userActionPause.viewportWidth || userActionPause.liveViewViewport?.width,
                      userActionPause.viewportHeight || userActionPause.liveViewViewport?.height,
                    );
                    window.open(href, "_blank", "noopener,noreferrer,width=1200,height=800");
                  }}
                >
                  Open Live View in new window
                </Button>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                Live View URL unavailable
                {userActionPause?.liveViewError ? `: ${userActionPause.liveViewError}` : "."}
                {" "}Keep the session warm and complete login in another window if needed.
              </Typography>
            )}
            <Button
              variant="contained"
              color="primary"
              disabled={loading || continuingUserAction || typeof onContinueUserAction !== "function"}
              onClick={() => onContinueUserAction && onContinueUserAction(userActionPause)}
            >
              {continuingUserAction ? "Continuing…" : "Continue"}
            </Button>
          </Paper>
        )}

        {currentReasoning ? renderReasoningBlock(currentReasoning, "Next") : null}
        {hasCurrentActions ? renderProposedActionsList(currentProposedActions, true) : null}
        {!hasCurrentActions && steps.length > 0 && !currentReasoning && !loading && !showRunResult && (
          <Paper variant="outlined" style={{ padding: 12, marginBottom: 16, backgroundColor: "rgba(0,0,0,0.03)" }}>
            <Typography variant="body2" color="textSecondary">No further actions for this cycle. Send a reply to guide the agent or close.</Typography>
          </Paper>
        )}

        {showRunResult && (
          <Paper
            variant="outlined"
            style={{
              padding: 14,
              marginBottom: 16,
              backgroundColor: success ? "rgba(76, 175, 80, 0.08)" : "rgba(244, 67, 54, 0.08)",
              borderColor: success ? "rgba(76, 175, 80, 0.3)" : "rgba(244, 67, 54, 0.3)",
            }}
          >
            <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 4 }}>
              Last run
            </Typography>
            <Typography variant="body2">
              {success
                ? resultCount === 0
                  ? "Run completed."
                  : `${resultCount} action(s) ran successfully.${loading ? " Getting next step…" : ""}`
                : (typeof runResult.error === "string" ? runResult.error : runResult.error?.message) || "Something went wrong running the approved actions."}
            </Typography>
          </Paper>
        )}

        {canReply && (
          <Box mt={2}>
            <TextField
              fullWidth
              size="small"
              placeholder="Reply to the agent (e.g. change something, or add a step…)"
              value={typeof replyText === "string" ? replyText : ""}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), handleSendReply())}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <Button
                    size="small"
                    color="primary"
                    disabled={loading || !String(replyText || "").trim()}
                    onClick={handleSendReply}
                    style={{ minWidth: 40 }}
                  >
                    <SendIcon fontSize="small" />
                  </Button>
                ),
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {waitingForFormSample ? (
          <Button onClick={handleDialogClose} disabled={formPreparing}>
            Cancel
          </Button>
        ) : isInitialPrep ? (
          <Button onClick={handleDialogClose} disabled={loading}>
            Cancel
          </Button>
        ) : !hasCurrentActions && !loading && !showRunResult ? (
          <Button variant="contained" color="primary" onClick={handleDialogClose}>
            Close
          </Button>
        ) : (
          <>
            <Button onClick={handleDialogClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="contained"
              color="primary"
              onClick={handleRunApproved}
              disabled={loading || !canRun || pausedForUser}
            >
              {loading
                ? "Working…"
                : pausedForUser
                  ? "Waiting for Continue…"
                  : useProposalIndexAsSelection
                    ? `Run ${indicesToRun.length} step(s)`
                    : `Run ${approvedIds.length} approved`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PreviewAgentModal;
